import { define, emit, EVENTS, routes, afterAdd, announce } from './theme.js';
import { enqueueCartMutation } from './cart-api.js';
class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.addEventListener('submit', (event) => this.add(event), { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
  async add(event) {
    event.preventDefault();
    if (this.busy) return;
    const form = event.target;
    const button = form.querySelector('button');
    const error = this.querySelector('[data-quick-add-error]');
    const body = new FormData(form);
    this.busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    error.hidden = true;
    try {
      await enqueueCartMutation(async () => {
        const sectionIds = [...new Set([...document.querySelectorAll('cart-items')].map((item) => item.dataset.sectionId))];
        body.set('sections', sectionIds.join(','));
        body.set('sections_url', window.location.pathname);
        const response = await fetch(`${routes().root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.dataset.error);
        if (!document.querySelector('cart-drawer') || sectionIds.some((id) => !result.sections?.[id])) { window.location.assign(`${routes().root}cart`); return; }
        emit(EVENTS.cartUpdate, { sections: result.sections, source: 'quick-add' });
        if (this.dataset.added) announce(this.dataset.added.replace('__title__', result.product_title || result.title || ''));
        afterAdd(button);
      });
    } catch (failure) {
      error.textContent = failure.message || this.dataset.error;
      error.hidden = false;
    } finally {
      this.busy = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}
define('quick-add', QuickAdd);

class QuickView extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.addEventListener('click', event => {
      const link = event.target.closest('[data-quick-view]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(link);
    }, options);
    document.addEventListener(EVENTS.cartOpen, event => {
      if (!this.dialog?.open) return;
      event.detail.trigger = this.querySelector('[data-quick-view]');
      this.dialog.close();
    }, { ...options, capture: true });
  }
  disconnectedCallback() { this.controller?.abort(); this.request?.abort(); this.dialog?.remove(); }
  async open(link) {
    if (this.busy) return;
    this.busy = true;
    this.request = new AbortController();
    link.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(link.href); url.searchParams.set('section_id', 'quick-view');
      const response = await fetch(url, { signal: this.request.signal });
      if (!response.ok) throw new Error('Quick view unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      doc.querySelectorAll('link[rel="stylesheet"]').forEach(style => {
        if (![...document.querySelectorAll('link[rel="stylesheet"]')].some(existing => existing.href === style.href)) document.head.append(style.cloneNode());
      });
      const content = doc.querySelector('[data-quick-view-content]');
      if (!content) throw new Error('Quick view unavailable');
      document.querySelectorAll('.quick-view-dialog').forEach(dialog => dialog.close());
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'quick-view-dialog';
      this.dialog.setAttribute('aria-label', this.dataset.title);
      const close = document.createElement('button'); close.type = 'button'; close.className = 'quick-view-close'; close.textContent = '×'; close.setAttribute('aria-label', this.dataset.close);
      close.addEventListener('click', () => this.dialog.close());
      this.dialog.append(close, content);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => { this.dialog.remove(); link.focus(); }, { once: true });
      content.querySelectorAll('variant-picker').forEach(picker => picker.removeAttribute('update-url'));
      document.body.append(this.dialog);
      await Promise.all(['product-form.js', 'variant-picker.js', 'product-gallery.js'].map(file => import(new URL(file, import.meta.url).href)));
      this.dialog.showModal(); close.focus();
      window.Shopify?.PaymentButton?.init();
    } catch (error) { if (error.name !== 'AbortError') location.assign(link.href); }
    finally { this.busy = false; link.removeAttribute('aria-busy'); }
  }
}
define('quick-view', QuickView);
